'use client'

import { css } from '@lastbrand/web-ui-styled-system/css'

import ProductPriceComparisonDS from '@src/components/blocks/product-price-comparison-ds'
import { PLTypedWidgetProps } from '@src/components/pl-base-widget'

import { usePdpSectionImpressionRef } from '../use-ia-impression'

import { toComparisonProduct } from './brand-comparison.adapter'
import { useBrandComparisonData } from './use-brand-comparison-data'

import type { BrandComparisonWidgetData } from '@lastbrand/storefront-proto-registry/api/v1/widget'

const containerCss = css({
  // Owns the accordion→table seam, matching non-PL `brandComparisonDsClass`. It rides on this widget
  // (which renders nothing at all when there is no comparison data) rather than on the ACCORDION's
  // `pb`, whose shell survives an empty render and would take the gap down with it.
  mt: 'space-24',
  mx: { base: 'auto', lg: '0' },
  mb: '0',
  // Figma: the section ends the mobile hero column with space-48; desktop carries no padding of its
  // own (the hero row's frame owns the row-bottom gap). Matches the non-PL surface, where the same
  // 48/0 split comes from the mobile column-end wrapper (`dsHeroColumnEndMobileClass`).
  pb: { base: 'space-48', lg: '0' },
  // Figma mobile column (390 + 768 frames, both under the 1200px `lg` breakpoint): the table is inset
  // space-16 each side. Same `calc` + `mx: auto` pattern as the sibling buy box (`ProductFormWrapper`);
  // desktop drops the inset and fills the 480px form column.
  width: { base: 'calc(100% - token(spacing.space-32))', lg: '100%' },
  maxWidth: { lg: '480px' },
})

// Telemetry action id (session-stream `entity_id`) — a stable identifier, not user-facing copy.
const BRAND_COMPARISON_ACTION = 'brand_comparision'

/**
 * DS 2.0 BRAND_COMPARISON render — hands off to the shared `ProductPriceComparisonDS` block (its own
 * layout owns spacing, so no legacy SCSS wrapper). The DS block derives `isDogFood`/`showPricePerUnit`
 * from `product.classification`, matching the legacy booleans. Served when the design system is enabled.
 */
export function BrandComparisonDSWidget(props: PLTypedWidgetProps<BrandComparisonWidgetData>) {
  // IA-only `brand_comparision` impression on scroll-into-view — mirrors the non-PL BrandComparisonProduct
  // section (`entity_id: brand_comparision`, `event_type: is_visible`, context `{ productId, selectedColor }`).
  // Non-PL fires this from a single component that toggles DS/legacy on one div+ref; PL splits that into two
  // widgets and renders only one at runtime, so the same impression lives here + in the legacy variant.
  const impressionRef = usePdpSectionImpressionRef({
    action: BRAND_COMPARISON_ACTION,
  })

  const model = useBrandComparisonData(props)
  if (!model) return null

  const { data, derived, brands, quincePrice, giftCardInformation } = model

  return (
    <div className={containerCss} ref={impressionRef}>
      <ProductPriceComparisonDS
        product={toComparisonProduct(data?.classification)}
        quincePrice={quincePrice}
        productComparisionsBrands={brands}
        hideFreeReturns={data?.hideFreeReturns}
        hasRestockingFee={data?.hasRestockingFee}
        showDollarComparisonPrice={false}
        showGiftCardCredit={derived?.giftCardSupported}
        giftCardInformation={giftCardInformation}
        servings={derived?.servings}
        pricePerServing={derived?.pricePerServing}
        hideSavings={data?.hideSavings}
      />
    </div>
  )
}
